import {
  Box,
  DropdownSelect,
  FieldText,
  FlexBox,
  Icon,
  IconButton,
  SelectItem,
  Text,
} from '@dixa/lib-shared-habla';
import noop from 'lodash/noop';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';

import styles from './authorizationHeader.module.scss';
import {
  AuthorizationItem,
  FieldInput,
  HeaderType,
} from './authorizationHeader.types';
import {
  authorizationItems,
  encodeHeader,
  getHeaderType,
  headerKey,
} from './authorizationHeader.utils';

type AuthorizationHeaderProps = {
  input: FieldInput;
};

export function AuthorizationHeader({
  input,
}: AuthorizationHeaderProps): JSX.Element {
  const [type, setType] = useState<HeaderType | null>();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);

  const { value, onChange } = input;

  const triggerOnChange = (value?: string) => {
    onChange(value ? encodeHeader(value, type) : {});
  };

  const onTypeSelect = (item: AuthorizationItem) => {
    if (isEditMode && item.id === type) {
      return;
    }

    setIsEditMode(true);
    setType(item.id);
    clearFields();
  };

  const onClearPreview = () => {
    setIsEditMode(false);
    setType(null);
    clearFields();
  };

  const clearFields = () => {
    setUsername('');
    setPassword('');
    setToken('');
    triggerOnChange('');
  };

  useEffect(() => {
    if (value[headerKey]) {
      setType(getHeaderType(value));
    }
  }, [value]);

  useEffect(() => {
    if (!!username.trim() && !!password.trim()) {
      triggerOnChange(`${username}:${password}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, password]);

  useEffect(() => {
    if (token) {
      triggerOnChange(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleAuthorizationChange = useCallback(
    (event?: ChangeEvent<HTMLInputElement>, key?: string) => {
      if (event) {
        const {
          target: { value },
        } = event;

        if (key === 'username') {
          setUsername(value);
        } else {
          setPassword(value);
        }
      }
    },
    [],
  );

  const handleTokenChange = useCallback(
    (event?: ChangeEvent<HTMLInputElement>) => {
      if (event) {
        const {
          target: { value },
        } = event;
        setToken(value);
      }
    },
    [],
  );

  const renderContent = () => {
    if (!type) {
      return null;
    }

    const authorizationName = authorizationItems.find(
      (item) => item.id === type,
    )?.name;

    if (isEditMode) {
      return (
        <FlexBox className={styles.content} flexDirection="row">
          {type === 'basic' ? (
            <>
              <FieldText
                className={styles.control}
                label="Username"
                input={{
                  name: 'username',
                  value: username ?? '',
                  onChange: (event) =>
                    handleAuthorizationChange(event, 'username'),
                }}
                placeholder="Type username"
              />
              <FieldText
                className={styles.control}
                label="Password"
                input={{
                  name: 'password',
                  value: password ?? '',
                  onChange: (event) =>
                    handleAuthorizationChange(event, 'password'),
                }}
                type="password"
                placeholder="Type password"
              />
            </>
          ) : (
            <FieldText
              className={styles.control}
              label="Token"
              input={{
                name: 'token',
                value: token ?? '',
                onChange: handleTokenChange,
              }}
              placeholder="Type token"
            />
          )}
        </FlexBox>
      );
    }

    return (
      <FlexBox className={styles.content} flexDirection="row">
        <FieldText
          className={styles.control}
          label={`${authorizationName} header`}
          disabled={true}
          input={{
            name: headerKey,
            value: input.value[headerKey] || '',
            onChange: noop,
          }}
        />
        <IconButton
          icon="trash"
          variant="tertiary"
          className={styles.button}
          onClick={onClearPreview}
        />
      </FlexBox>
    );
  };

  return (
    <Box className={styles.root}>
      <FlexBox
        className={styles.header}
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text.Heading variant="heading-3">Authorization</Text.Heading>
        <DropdownSelect
          items={authorizationItems}
          itemComponent={SelectItem}
          buttonProps={{
            variant: 'tertiary',
          }}
          onSelect={onTypeSelect}
        >
          {type ? <Icon name="pen" /> : <Icon name="plus" />}
        </DropdownSelect>
      </FlexBox>
      {renderContent()}
    </Box>
  );
}
